import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../utils/supabase';
import {
  getAllSessions,
  fetchSessionSignupCountsMap,
  mergeSessionSignupCounts,
} from '../services/sessionManagementService';
import {
  fetchSessionActivityCountMap,
  mergeSessionActivityCounts,
} from '../services/sessionActivityStatsService';
import {
  buildCompletedSessionAllMarked,
  categorizeSessionsByTime,
  getTodayWorkItems,
  filterUpcomingSessions,
  getTodayDateString,
} from '../components/sessions/hub/sessionHubUtils';

const SESSION_SELECT = `
  *,
  session_types:session_type_id (
    id,
    name,
    description
  ),
  session_series:session_series_id (
    id,
    name,
    is_active
  )
`;

async function fetchPaymentMarkedMap(completedIds) {
  if (!completedIds.length) return {};

  const { data: playersRows } = await supabase
    .from('session_players')
    .select('id, session_id, exit_reason')
    .in('session_id', completedIds);

  const sessionPlayerIds = (playersRows || []).map((sp) => sp.id);
  if (!sessionPlayerIds.length) {
    const empty = {};
    completedIds.forEach((id) => {
      empty[id] = true;
    });
    return empty;
  }

  const { data: paidAllocations } = await supabase
    .from('payment_allocations')
    .select(`
      session_player_id,
      payment_ledger:payment_id!inner(is_deleted)
    `)
    .in('session_player_id', sessionPlayerIds);

  const paidSessionPlayerIds = new Set();
  (paidAllocations || []).forEach((alloc) => {
    if (alloc.payment_ledger && !alloc.payment_ledger.is_deleted) {
      paidSessionPlayerIds.add(alloc.session_player_id);
    }
  });

  return buildCompletedSessionAllMarked(playersRows, paidSessionPlayerIds);
}

async function fetchSessionsWithPaymentsMap(sessionIds) {
  if (!sessionIds.length) return {};

  const paymentsCheck = {};
  const { data: paidSessionPlayers } = await supabase
    .from('payment_allocations')
    .select(`
      session_player:session_player_id!inner(session_id),
      payment_ledger:payment_id!inner(is_deleted)
    `)
    .in('session_player.session_id', sessionIds);

  (paidSessionPlayers || []).forEach((row) => {
    if (row.payment_ledger && !row.payment_ledger.is_deleted && row.session_player) {
      paymentsCheck[row.session_player.session_id] = true;
    }
  });

  return paymentsCheck;
}

function fetchStaleUpcomingSessions(clubId, todayStr) {
  return supabase
    .from('sessions')
    .select(SESSION_SELECT)
    .eq('club_id', clubId)
    .in('session_status', ['upcoming', 'draft'])
    .lt('session_date', todayStr)
    .not('session_date', 'is', null)
    .order('session_date', { ascending: false });
}

function mergeUpcomingLists(upcomingData, staleUpcoming) {
  const upcomingById = new Map();
  (upcomingData || []).forEach((session) => upcomingById.set(session.id, session));
  (staleUpcoming || []).forEach((session) => upcomingById.set(session.id, session));
  return Array.from(upcomingById.values());
}

async function fetchPlannedSessionList(clubId, filters, todayStr) {
  const [upcomingData, staleResult] = await Promise.all([
    getAllSessions(clubId, filters),
    fetchStaleUpcomingSessions(clubId, todayStr),
  ]);
  if (staleResult.error) throw staleResult.error;
  const merged = mergeUpcomingLists(upcomingData, staleResult.data);
  const countMap = await fetchSessionSignupCountsMap(merged.map((session) => session.id));
  return mergeSessionSignupCounts(merged, countMap);
}

/**
 * Shared sessions hub data: upcoming list, today's work inbox, past sessions grouping.
 * Scopes skip work the page does not need:
 * - `start`: planned + live
 * - `plan`: planned only
 * - `review`: completed inbox + payment maps (Past/Archived fetch themselves)
 */
export function useClubSessionsHub(clubId, { includeCancelled = false, scope = 'full' } = {}) {
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [completedSessionAllMarked, setCompletedSessionAllMarked] = useState({});
  const [sessionsWithPayments, setSessionsWithPayments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHubData = useCallback(async () => {
    if (!clubId) return;

    setLoading(true);
    setError(null);

    try {
      const filters = includeCancelled ? {} : { excludeCancelled: true };
      const todayStr = getTodayDateString();

      if (scope === 'plan') {
        const mergedUpcoming = await fetchPlannedSessionList(clubId, filters, todayStr);
        setUpcomingSessions(mergedUpcoming);
        setPastSessions([]);
        setSessionsWithPayments({});
        setCompletedSessionAllMarked({});
        return;
      }

      if (scope === 'start') {
        const [mergedUpcoming, liveResult] = await Promise.all([
          fetchPlannedSessionList(clubId, filters, todayStr),
          supabase
            .from('sessions')
            .select(SESSION_SELECT)
            .eq('club_id', clubId)
            .eq('session_status', 'ongoing')
            .order('session_date', { ascending: false }),
        ]);

        if (liveResult.error) throw liveResult.error;

        const liveList = liveResult.data || [];
        const activityCountMap = await fetchSessionActivityCountMap(liveList.map((s) => s.id));

        setUpcomingSessions(mergeSessionActivityCounts(mergedUpcoming, activityCountMap));
        setPastSessions(mergeSessionActivityCounts(liveList, activityCountMap));
        setSessionsWithPayments({});
        setCompletedSessionAllMarked({});
        return;
      }

      if (scope === 'review') {
        const { data: completedData, error: completedError } = await supabase
          .from('sessions')
          .select(SESSION_SELECT)
          .eq('club_id', clubId)
          .eq('session_status', 'completed')
          .order('session_date', { ascending: false });

        if (completedError) throw completedError;

        const completedList = completedData || [];
        const completedIds = completedList.map((s) => s.id);
        const [activityCountMap, paymentsMap, markedMap] = await Promise.all([
          fetchSessionActivityCountMap(completedIds),
          fetchSessionsWithPaymentsMap(completedIds),
          fetchPaymentMarkedMap(completedIds),
        ]);

        setUpcomingSessions([]);
        setPastSessions(mergeSessionActivityCounts(completedList, activityCountMap));
        setSessionsWithPayments(paymentsMap);
        setCompletedSessionAllMarked(markedMap);
        return;
      }

      const [upcomingData, staleResult, pastResult] = await Promise.all([
        getAllSessions(clubId, filters),
        fetchStaleUpcomingSessions(clubId, todayStr),
        supabase
          .from('sessions')
          .select(SESSION_SELECT)
          .eq('club_id', clubId)
          .neq('session_status', 'draft')
          .in('session_status', ['ongoing', 'completed', 'archived', 'cancelled'])
          .order('session_date', { ascending: false }),
      ]);

      if (staleResult.error) throw staleResult.error;
      if (pastResult.error) throw pastResult.error;

      const mergedUpcoming = mergeUpcomingLists(upcomingData, staleResult.data);
      const pastList = pastResult.data || [];

      const activitySessionIds = [
        ...new Set(
          [...mergedUpcoming, ...pastList]
            .filter((s) =>
              ['ongoing', 'completed', 'archived'].includes(s.session_status)
            )
            .map((s) => s.id)
        ),
      ];
      const activityCountMap = await fetchSessionActivityCountMap(activitySessionIds);

      setUpcomingSessions(mergeSessionActivityCounts(mergedUpcoming, activityCountMap));
      setPastSessions(mergeSessionActivityCounts(pastList, activityCountMap));

      const paymentCheckIds = pastList.map((s) => s.id);
      const completedIds = pastList
        .filter((s) => s.session_status === 'completed')
        .map((s) => s.id);
      const [paymentsMap, markedMap] = await Promise.all([
        fetchSessionsWithPaymentsMap(paymentCheckIds),
        fetchPaymentMarkedMap(completedIds),
      ]);
      setSessionsWithPayments(paymentsMap);
      setCompletedSessionAllMarked(markedMap);
    } catch (err) {
      console.error('[useClubSessionsHub] fetch error:', err);
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [clubId, includeCancelled, scope]);

  useEffect(() => {
    fetchHubData();
  }, [fetchHubData]);

  const inboxSourceSessions = useMemo(() => {
    const byId = new Map();
    (pastSessions || []).forEach((s) => {
      if (s.session_status === 'ongoing' || s.session_status === 'completed') {
        byId.set(s.id, s);
      }
    });
    (upcomingSessions || []).forEach((s) => {
      const existing = byId.get(s.id);
      if (existing) {
        byId.set(s.id, {
          ...existing,
          ...s,
          games_count: existing.games_count ?? s.games_count,
          players_count: existing.players_count ?? s.players_count,
        });
      } else {
        byId.set(s.id, s);
      }
    });
    return Array.from(byId.values());
  }, [upcomingSessions, pastSessions]);

  const todayWorkData = useMemo(
    () =>
      getTodayWorkItems(
        inboxSourceSessions,
        completedSessionAllMarked,
        sessionsWithPayments
      ),
    [inboxSourceSessions, completedSessionAllMarked, sessionsWithPayments]
  );

  const upcoming = useMemo(
    () => filterUpcomingSessions(upcomingSessions),
    [upcomingSessions]
  );

  const past = useMemo(() => categorizeSessionsByTime(pastSessions), [pastSessions]);

  return {
    upcoming,
    upcomingSessions,
    pastSessions,
    past,
    todayWork: todayWorkData.items,
    todayWorkOverflowWrapUpCount: todayWorkData.overflowWrapUpCount,
    completedSessionAllMarked,
    loading,
    error,
    refresh: fetchHubData,
  };
}

export default useClubSessionsHub;
